import React, { useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import filter from 'lodash/filter';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import find from 'lodash/find';
import UserDatabases from '@Services/UserDatabases';
import Catalyst from '@Services/Catalyst';
import AccordionHeader from '@Components/Results/common/AccordionHeader';
import { REACTION_ADDITIONAL_INFO_VIEW_MODES } from '@Constants/ElementConstants';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ReactTableColumnSwitcherHelper from '@Components/Results/services/react-table-columns-switcher-helper.service';
import ReactionAdditionalDataContents from '@Components/Results/reactions/reaction-details/additional-data/ReactionAdditionalDataContents';
import TableColumnsSwitcher from '@Components/Results/reactions/reaction-details/table-columns-switcher/TableColumnsSwitcher';

import './ReactionAdditionalData.scss';

function ReactionAdditionalData({ data, viewMode, accordionTitle, uniqueId, databaseId, reactionId, stages, roles }) {
  const containerRef = useRef(null);
  const columnSwitcherRef = useRef(null);

  // Helper functions
  const mapReactantInfo = useCallback(
    (reactant, headers) =>
      headers.map((header) => {
        const value = reactant?.[header.id] || '';
        const appendix =
          header.appendix && value && (!header.appendixIgnoreIf || !value.match(header.appendixIgnoreIf)) ? ` ${header.appendix}` : '';
        return {
          order: header.order,
          id: header.id,
          value: value + appendix,
        };
      }),
    []
  );

  const markContentExists = useCallback((rows, headers) => {
    rows.forEach((row) => {
      row.cells = sortBy(row.cells, 'order');
      row.cells.forEach((cell) => {
        if (cell?.value) {
          const headerItem = find(headers, { id: cell.id });
          if (headerItem) headerItem.hasContent = true;
        }
      });
    });
    return rows;
  }, []);

  const markLeadingTrailingStages = useCallback((rows) => {
    const lastIndex = rows?.length - 1 || 0;

    rows?.forEach((row, i) => {
      const currentStage = ReactReactionAdditionalInfoHelper.getStage(row);
      const nextStage = ReactReactionAdditionalInfoHelper.getStage(rows[i + 1]);
      const prevStage = ReactReactionAdditionalInfoHelper.getStage(rows[i - 1]);

      if (i < lastIndex && currentStage === nextStage) {
        row.leading = true;
      }
      if (i > 0 && currentStage === prevStage) {
        row.trailing = true;
      }
    });

    return rows;
  }, []);

  // Initial data formatting
  const initialFormatedData = useMemo(() => {
    const allHeaders = { ...REACTION_ADDITIONAL_INFO_VIEW_MODES[viewMode] };
    const shownHeaders = filter(allHeaders, (header) => {
      const matchStages = !header.stages || stages;
      const matchRoles = !header.roles || roles;
      return matchStages && matchRoles;
    });

    let rows = map(data, (dataItem) => ({
      id: dataItem?.id,
      cells: mapReactantInfo(dataItem, shownHeaders),
    }));

    rows = markContentExists(rows, shownHeaders);
    rows = markLeadingTrailingStages(rows);

    return {
      layout: 'list',
      headers: shownHeaders,
      rows,
    };
  }, [data, viewMode, stages, roles, mapReactantInfo, markContentExists, markLeadingTrailingStages]);

  const [formatedData, setFormatedData] = useState(initialFormatedData);
  const [isOpen, setIsOpen] = useState(false);

  // Handlers
  const toggleAccordion = useCallback(() => {
    if (!isOpen) {
      const dbName = UserDatabases.getDbName(databaseId);
      Catalyst.trackContentView('contentView', {
        id: `contentview:${reactionId}`,
        type: `${Catalyst.getPrefix()}:contentview:${dbName}:${accordionTitle}`,
      });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, accordionTitle, reactionId, databaseId]);

  const updateHeaders = useCallback((headers) => {
    setFormatedData((prev) => ({
      ...prev,
      headers,
    }));
  }, []);

  const showAllNonEmptyCols = useCallback(() => {
    columnSwitcherRef?.current?.showAllNonEmptyCols?.();
  }, []);

  return (
    <div className={`result-accordion-item ${isOpen ? '' : 'closed'}`}>
      <AccordionHeader isOpen={isOpen} accordionId={uniqueId} toggle={toggleAccordion}>
        {accordionTitle}
      </AccordionHeader>

      {isOpen && (
        <div id={uniqueId} className="rx-accordion-content u-els-padding-top-1x" aria-expanded={isOpen} ref={containerRef}>
          <div className="rx-accordion-content additional-data-tables">
            <div className="switcher-wrapper">
              <TableColumnsSwitcher
                headers={formatedData.headers}
                uniqueId={uniqueId}
                containerRef={containerRef}
                ref={columnSwitcherRef}
                updateHeaders={updateHeaders}
              />
            </div>
            <style>{ReactTableColumnSwitcherHelper.generateCssRules(uniqueId, formatedData.headers)}</style>
            <ReactionAdditionalDataContents
              databaseId={databaseId}
              uniqueId={uniqueId}
              formatedData={formatedData}
              showAllNonEmptyCols={showAllNonEmptyCols}
            />
          </div>
        </div>
      )}
    </div>
  );
}

ReactionAdditionalData.propTypes = {
  data: PropTypes.array.isRequired,
  viewMode: PropTypes.string.isRequired,
  accordionTitle: PropTypes.string.isRequired,
  uniqueId: PropTypes.string.isRequired,
  databaseId: PropTypes.string.isRequired,
  reactionId: PropTypes.string,
  stages: PropTypes.string,
  roles: PropTypes.string,
};

export default ReactionAdditionalData;
